import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import axiosClient from '../../api/axiosClient';
import { DollarSign, Clock, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const COLORS = ['#10b981', '#f43f5e', '#3b82f6', '#f59e0b'];

export default function AdminAnalyticsDashboard() {
  const [data, setData] = useState({
    mrr: 0,
    conversionRate: 0,
    resolutionTime: "0",
    revenueData: [],
    conversionData: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axiosClient.get('/admin/analytics');
      setData({
        mrr: res.data?.mrr ?? 0,
        conversionRate: res.data?.conversionRate ?? 0,
        resolutionTime: res.data?.resolutionTime ?? "0",
        revenueData: res.data?.revenueData ?? [],
        conversionData: res.data?.conversionData ?? []
      });
    } catch (err) {
      console.error('Failed to fetch analytics', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="h-80 bg-white dark:bg-[#1e2336] rounded-2xl animate-pulse"></div>
        <div className="h-80 bg-white dark:bg-[#1e2336] rounded-2xl animate-pulse"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mt-8 space-y-6"
    >
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 rounded-2xl text-white shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-20"><DollarSign size={80} /></div>
          <h3 className="text-white/80 font-medium mb-2">Monthly Recurring Revenue (MRR)</h3>
          <p className="text-4xl font-bold">₹{data.mrr}</p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 rounded-2xl text-white shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-20"><Users size={80} /></div>
          <h3 className="text-white/80 font-medium mb-2">Trial Conversion Rate</h3>
          <p className="text-4xl font-bold">{data.conversionRate}%</p>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-6 rounded-2xl text-white shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-20"><Clock size={80} /></div>
          <h3 className="text-white/80 font-medium mb-2">Avg Enquiry Resolution</h3>
          <p className="text-4xl font-bold">{data.resolutionTime}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Revenue Line Chart */}
        <div className="bg-white dark:bg-[#1e2336] p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Revenue Growth</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.revenueData ?? []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} tickFormatter={(val) => `₹${val}`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  itemStyle={{ color: '#4f46e5', fontWeight: 600 }}
                  formatter={(value) => [`₹${value}`, "Revenue"]}
                />
                <Line type="monotone" dataKey="revenue" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4, fill: '#4f46e5', strokeWidth: 2 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Conversion Pie Chart */}
        <div className="bg-white dark:bg-[#1e2336] p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Trial Conversions</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.conversionData ?? []}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {(data.conversionData ?? []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
